// Intelligent caching strategy for photo application
// Uses IndexedDB for async, high-capacity storage (replaces localStorage)

class PhotoCacheStrategy {
  constructor() {
    this.version = 'v5'; // Bump version to invalidate old caches
    this.dbName = 'dailey-photos-cache';
    this.storeName = 'entries';
    this.db = null;
    this.dbReady = null; // Promise that resolves when DB is open
    this.maxBudgetBytes = 50 * 1024 * 1024; // 50MB budget

    // Open DB immediately
    this.dbReady = this._openDB();

    // Clean up old localStorage caches from previous versions
    this._cleanupLocalStorage();

    // Cross-tab communication
    this.broadcastChannel = null;
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('dailey-photos-cache');
      this.broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'CACHE_INVALIDATE') {
          this.clearType(event.data.cacheType, event.data.userId);
        }
      };
    }

    // Cache durations for different data types
    this.cacheDurations = {
      albums: 20 * 60 * 1000,
      albumImages: 10 * 60 * 1000,
      tags: 30 * 60 * 1000,
      shares: 10 * 60 * 1000,
      recentImages: 10 * 60 * 1000,
      favorites: 2 * 60 * 1000,
      searchResults: 5 * 60 * 1000,
      userProfile: 2 * 60 * 1000,
      uploadProgress: 0,
    };

    // Mutation → cache type invalidation map
    this.mutationMap = {
      'like': ['images', 'favorites', 'albumImages', 'recentImages'],
      'unlike': ['images', 'favorites', 'albumImages', 'recentImages'],
      'upload': ['images', 'albums', 'recentImages', 'tags'],
      'delete': ['images', 'albums', 'albumImages', 'favorites', 'recentImages', 'shares'],
      'addToAlbum': ['albums', 'albumImages', 'shares'],
      'removeFromAlbum': ['albums', 'albumImages', 'shares'],
      'createAlbum': ['albums'],
      'deleteAlbum': ['albums', 'shares'],
      'tag': ['images', 'tags', 'albumImages', 'recentImages'],
      'untag': ['images', 'tags', 'albumImages', 'recentImages'],
      'editImage': ['images', 'albumImages', 'recentImages'],
      'editExif': ['images', 'albumImages'],
      'edit': ['images', 'albumImages', 'recentImages'],
      'share': ['shares', 'albums'],
      'unshare': ['shares', 'albums'],
      'watermark': ['shares']
    };

    // Prefetch patterns
    this.prefetchPatterns = {
      'viewAlbum': (albumId) => [`album_${albumId}_images`],
      'viewImage': (imageId, albumId) => [
        `image_${imageId}_next`,
        `image_${imageId}_prev`,
        albumId ? `album_${albumId}_images` : null
      ].filter(Boolean),
      'openLightbox': (images, currentIndex) => {
        const prefetch = [];
        for (let i = Math.max(0, currentIndex - 2); i <= Math.min(images.length - 1, currentIndex + 3); i++) {
          if (i !== currentIndex) {
            prefetch.push(`image_full_${images[i].id}`);
          }
        }
        return prefetch;
      }
    };
  }

  // --- IndexedDB internals ---

  _openDB() {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        // SSR or unsupported — degrade gracefully
        resolve(null);
        return;
      }
      const request = indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('by-type', 'type', { unique: false });
          store.createIndex('by-timestamp', 'timestamp', { unique: false });
        }
      };
      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };
      request.onerror = () => {
        console.warn('IndexedDB open failed, caching disabled');
        resolve(null);
      };
    });
  }

  async _getDB() {
    if (this.db) return this.db;
    await this.dbReady;
    return this.db;
  }

  // --- Key generation ---

  getCacheKey(type, id, userId) {
    return `${this.version}_${userId}_${type}${id ? `_${id}` : ''}`;
  }

  // --- Core API ---

  async set(type, data, { id, userId } = {}) {
    try {
      const db = await this._getDB();
      if (!db) return;

      const key = this.getCacheKey(type, id, userId);
      const ttl = this.cacheDurations[type] || 60000;

      const entry = {
        key,
        data,
        timestamp: Date.now(),
        ttl,
        version: this.version,
        type
      };

      const tx = db.transaction(this.storeName, 'readwrite');
      tx.objectStore(this.storeName).put(entry);
      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    } catch (e) {
      console.warn('Cache set failed:', e);
    }
  }

  async get(type, { id, userId } = {}) {
    try {
      const db = await this._getDB();
      if (!db) return null;

      const key = this.getCacheKey(type, id, userId);

      const tx = db.transaction(this.storeName, 'readonly');
      const request = tx.objectStore(this.storeName).get(key);
      const entry = await new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (!entry) return null;

      // Version mismatch — stale entry
      if (entry.version !== this.version) {
        this._deleteKey(key);
        return null;
      }

      // Expired
      const age = Date.now() - entry.timestamp;
      if (age > entry.ttl) {
        this._deleteKey(key);
        return null;
      }

      return {
        data: entry.data,
        age,
        fresh: age < entry.ttl * 0.25
      };
    } catch (e) {
      console.warn('Cache get failed:', e);
      return null;
    }
  }

  async invalidate(mutationType, { userId, imageId, albumId } = {}) {
    const typesToInvalidate = this.mutationMap[mutationType] || [];

    for (const type of typesToInvalidate) {
      if (type === 'albumImages' && albumId) {
        this.clear(type, { id: albumId, userId });
      } else if (type === 'images' && imageId) {
        this.clear('image', { id: imageId, userId });
      } else {
        this.clearType(type, userId);
      }

      // Broadcast to other tabs
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage({
          type: 'CACHE_INVALIDATE',
          cacheType: type,
          userId,
          mutationType
        });
      }

      // Notify Service Worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'INVALIDATE_API',
          cacheType: type,
          userId
        });
      }
    }

    // Also clear search results as they might be affected
    this.clearType('searchResults', userId);
  }

  async clear(type, { id, userId } = {}) {
    const key = this.getCacheKey(type, id, userId);
    await this._deleteKey(key);
  }

  async clearType(type, userId) {
    try {
      const db = await this._getDB();
      if (!db) return;

      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('by-type');
      const request = index.openCursor(IDBKeyRange.only(type));

      const prefix = userId ? `${this.version}_${userId}_` : `${this.version}_`;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.key.startsWith(prefix)) {
            cursor.delete();
          }
          cursor.continue();
        }
      };

      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    } catch (e) {
      console.warn('Cache clearType failed:', e);
    }
  }

  async clearUser(userId) {
    try {
      const db = await this._getDB();
      if (!db) return;

      const prefix = `${this.version}_${userId}_`;
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.key.startsWith(prefix)) {
            cursor.delete();
          }
          cursor.continue();
        }
      };

      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    } catch (e) {
      console.warn('Cache clearUser failed:', e);
    }
  }

  prefetch(pattern, ...args) {
    const keys = this.prefetchPatterns[pattern]?.(...args) || [];
    return keys;
  }

  // --- Internal helpers ---

  async _deleteKey(key) {
    try {
      const db = await this._getDB();
      if (!db) return;
      const tx = db.transaction(this.storeName, 'readwrite');
      tx.objectStore(this.storeName).delete(key);
    } catch (e) {
      // Ignore delete errors
    }
  }

  _cleanupLocalStorage() {
    // Remove old localStorage/sessionStorage entries from previous versions
    try {
      const oldVersions = ['v1', 'v2', 'v3', 'v4'];
      [localStorage, sessionStorage].forEach(storage => {
        const keysToRemove = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (!key) continue;
          for (const v of oldVersions) {
            if (key.startsWith(v + '_')) {
              keysToRemove.push(key);
              break;
            }
          }
        }
        keysToRemove.forEach(k => storage.removeItem(k));
        if (keysToRemove.length > 0) {
          console.log(`Cleaned up ${keysToRemove.length} old localStorage cache entries`);
        }
      });
    } catch (e) {
      // Ignore cleanup errors
    }
  }

  async getStats(userId) {
    try {
      const db = await this._getDB();
      if (!db) return { indexedDB: { count: 0, items: [] } };

      const prefix = userId ? `${this.version}_${userId}_` : this.version;
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const request = store.getAll();
      const entries = await new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const items = entries
        .filter(e => e.key.startsWith(prefix))
        .map(e => ({
          key: e.key,
          type: e.type,
          age: Math.round((Date.now() - e.timestamp) / 1000) + 's',
          expired: (Date.now() - e.timestamp) > e.ttl,
          size: JSON.stringify(e.data).length
        }));

      return {
        indexedDB: {
          count: items.length,
          size: items.reduce((sum, i) => sum + i.size, 0),
          items
        }
      };
    } catch (e) {
      return { indexedDB: { count: 0, error: e.message } };
    }
  }
}

// Export singleton instance
export const cacheStrategy = new PhotoCacheStrategy();

// React hook for cached data fetching
import { useState, useEffect } from 'react';

export function useCachedFetch(type, fetcher, options = {}) {
  const { id, userId, dependencies = [], prefetch = null } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        // Check cache first
        const cached = await cacheStrategy.get(type, { id, userId });

        if (cached && cached.fresh) {
          setData(cached.data);
          setLoading(false);
          return;
        } else if (cached) {
          setData(cached.data);
        }

        // Fetch fresh data
        const freshData = await fetcher();

        if (!cancelled) {
          setData(freshData);
          cacheStrategy.set(type, freshData, { id, userId });

          if (prefetch) {
            const prefetchKeys = cacheStrategy.prefetch(prefetch.pattern, ...prefetch.args);
            options.onPrefetch?.(prefetchKeys);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [type, id, userId, ...dependencies]);

  return { data, loading, error, invalidate: (mutation) => cacheStrategy.invalidate(mutation, { userId, imageId: id }) };
}
